import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import Loader from "../components/Loader";
import styled, { createGlobalStyle } from "styled-components";
import { Row, Col, Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserGroup } from "@fortawesome/free-solid-svg-icons";
import isEqual from "lodash/isEqual";
import { ModernButton } from "./DefaultImports";
import { CustomModal } from "../components/CustomModal";
import ReservationFloorPlan from "../place_design/ReservationFloorPlan.js";
import { api } from "../App";

function cleanList(list = []) {
  return (list || []).map((sublist) => {
    return (sublist || []).map((item) => {
      const splitItem = String(item).split(";");

      const reservationIds = splitItem[2]
        ? splitItem[2]
            .split(",")
            .map((id) => id.trim())
            .filter(
              (id) =>
                id && id !== "null" && id !== "undefined" && id !== "None",
            )
        : [];

      const rawStatus = String(splitItem[1] ?? "0");

      return {
        number: splitItem[0],
        status: rawStatus === "1" || reservationIds.length > 0 ? "1" : "0",
        reservationIds,
      };
    });
  });
}

function getInitialTableViewMode() {
  try {
    const saved = window.localStorage.getItem("soup_table_assignment_view");
    if (saved === "simple" || saved === "floor") return saved;
  } catch (e) {
    // ignore localStorage problems
  }

  return "simple";
}

function getReservationInfoText(table) {
  if (!table?.reservationIds?.length) {
    return "";
  }

  return `Ocupada pela reserva #${table.reservationIds.join(", #")}`;
}

function getCurrentReservationId(reservation) {
  if (reservation?.id) return String(reservation.id);
  if (reservation?.reservation_id) return String(reservation.reservation_id);
  if (reservation?.pk) return String(reservation.pk);

  return "";
}

function tableBelongsToCurrentReservation(table, reservation) {
  const currentReservationId = getCurrentReservationId(reservation);

  if (!currentReservationId) return false;

  return (table?.reservationIds || [])
    .map((id) => String(id))
    .includes(currentReservationId);
}

function isTableOccupied(table) {
  return (
    String(table?.status ?? "0") === "1" ||
    (table?.reservationIds || []).length > 0
  );
}

function isTableBlockedForCurrentReservation(table, reservation) {
  if (!isTableOccupied(table)) return false;

  if (tableBelongsToCurrentReservation(table, reservation)) return false;

  return true;
}

function getRestaurantIdFromContext(reservation, allTables) {
  if (reservation?.restaurant?.id) return reservation.restaurant.id;
  if (reservation?.restaurant?.pk) return reservation.restaurant.pk;
  if (reservation?.restaurant_id) return reservation.restaurant_id;

  if (typeof reservation?.restaurant === "number") {
    return reservation.restaurant;
  }

  const tableRestaurant = allTables?.[0]?.restaurant;

  if (typeof tableRestaurant === "number") return tableRestaurant;
  if (tableRestaurant?.id) return tableRestaurant.id;
  if (tableRestaurant?.pk) return tableRestaurant.pk;

  return null;
}

function mergeLayoutItems(existingItems = [], savedItems = []) {
  const map = new Map();

  existingItems.forEach((item) => {
    if (item?.table_id) {
      map.set(String(item.table_id), item);
    }
  });

  savedItems.forEach((item) => {
    if (item?.table_id) {
      map.set(String(item.table_id), item);
    }
  });

  return Array.from(map.values());
}

const ModalStackingFix = createGlobalStyle`
  .unsaved-reservation-layout-modal {
    z-index: 20000 !important;
  }

  .unsaved-reservation-layout-backdrop {
    z-index: 19990 !important;
  }
`;

const StyledH6 = styled.h6`
  color: ${({ enoughSeats }) => (enoughSeats ? "green" : "red")};
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 15px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
`;

const LegendColor = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background-color: ${({ color }) => color};
  border: 1px solid #bbbbbb;
`;

const ViewModeBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  margin-bottom: 14px;
  background: #faf6e9;
  border: 1px solid #eee;
  border-radius: 8px;
`;

const ViewModeText = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

const ViewModeButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const ToolDivider = styled.div`
  width: 1px;
  height: 26px;
  background: #ddd;
  margin: 0 2px;
`;

const ToolIconButton = styled.button`
  width: 34px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${({ active }) => (active ? "#EE754C" : "#ccc")};
  background: ${({ active }) => (active ? "#fff1e8" : "#fff")};
  color: ${({ active }) => (active ? "#EE754C" : "#4B3335")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #ee754c;
    background: #fff7f2;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ZonePickerBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const ZonePickerLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: #4b3335;
`;

const ZoneBubble = styled.button`
  border: 1px solid ${({ active }) => (active ? "#EE754C" : "#ddd")};
  background: ${({ active }) => (active ? "#fff1e8" : "#fff")};
  color: ${({ active }) => (active ? "#EE754C" : "#4B3335")};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #ee754c;
    background: #fff7f2;
  }
`;

const ZoneSelect = styled.select`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 0.9rem;
  color: #4b3335;
  background: #fff;
`;

const ZoneContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledTableDiv = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${({ isSelected }) =>
    isSelected ? "2px solid #ee754c" : "1px solid #bbb"};
  border-radius: 6px;
  padding: 6px;
  margin: 6px;
  font-weight: 500;
  font-size: 14px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  background-color: ${({ isSelected, tableStatus }) =>
    isSelected
      ? tableStatus === "1"
        ? "#EE561B"
        : "#f79574"
      : tableStatus === "1"
        ? "#99B0AD"
        : "#FFFFFF"};

  color: ${({ isSelected, tableStatus }) =>
    isSelected || tableStatus === "1" ? "white" : "#4B3335"};

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border-color: #ee754c;
  }
`;

const TablesModal = ({
  show,
  tables_list,
  onClose,
  initialSelectedTables,
  numPeople,
  time,
  date,
  handleTablesUsedChange,
  setSelectedTablesSave,
  setPlaceOfTable,
  placeOfTable,
  setEnoughSeatsReservation,
  reservation,
}) => {
  const is_production =
    process.env.REACT_APP_IS_PRODUCTION === "false" ? false : true;

  const tables = cleanList(tables_list);

  const zoneNames = (tables || [])
    .map((zoneTables) => zoneTables?.[0]?.number)
    .filter(Boolean);

  const zoneNamesKey = zoneNames.join("|");

  const all_tables = useSelector((state) => state.restaurantTables.tables);

  const connectTablesConfig = useSelector(
    (state) => state.connectTablesConfig.connectTablesConfig,
  );

  const [enoughSeats, setEnoughSeats] = useState(false);
  const [countPeople, setCountPeople] = useState(0);
  const [tablesConfigList, setTablesConfigList] = useState([]);
  const [selectedTables, setSelectedTables] = useState({});
  const [tableViewMode, setTableViewMode] = useState(getInitialTableViewMode);
  const [floorToolMode, setFloorToolMode] = useState("select");
  const [activeZoneName, setActiveZoneName] = useState("");

  const [reservationLayoutOverrides, setReservationLayoutOverrides] = useState(
    [],
  );
  const [reservationLayoutDraftItems, setReservationLayoutDraftItems] =
    useState([]);
  const [isLoadingReservationLayout, setIsLoadingReservationLayout] =
    useState(false);
  const [isSavingReservationLayout, setIsSavingReservationLayout] =
    useState(false);
  const [
    showUnsavedReservationLayoutModal,
    setShowUnsavedReservationLayoutModal,
  ] = useState(false);

  const selectedTablesRef = useRef({});
  const selectedTablesChangeSourceRef = useRef("hydrate");
  const lastSelectedTablesWriteKeyRef = useRef("");

  const lastSeenPlaceOfTableRef = useRef(placeOfTable || "");
  const pendingPlaceSuggestionRef = useRef(false);
  const lastAppliedPlaceSuggestionKeyRef = useRef("");
  const placeChangeVersionRef = useRef(0);
  const skipNextPlaceSuggestionRef = useRef(false);

  const lastHydratedInitialTablesKeyRef = useRef("");
  const lastHydratedReservationKeyRef = useRef("");
  const lastScenarioRef = useRef("");
  const lastPeopleSnapshotRef = useRef({
    reservationId: "",
    numPeople: null,
  });

  const getReservationLayoutContext = () => {
    const restaurantId = getRestaurantIdFromContext(reservation, all_tables);
    const reservationId = getCurrentReservationId(reservation);

    return {
      restaurantId,
      reservationId,
    };
  };

  useEffect(() => {
    selectedTablesRef.current = selectedTables;
  }, [selectedTables]);

  useEffect(() => {
    try {
      window.localStorage.setItem("soup_table_assignment_view", tableViewMode);
    } catch (e) {
      // ignore localStorage problems
    }
  }, [tableViewMode]);

  useEffect(() => {
    if (tableViewMode !== "floor") {
      setFloorToolMode("select");
    }
  }, [tableViewMode]);

  useEffect(() => {
    if (!zoneNames.length) {
      setActiveZoneName("");
      return;
    }

    setActiveZoneName((currentZoneName) => {
      if (placeOfTable && zoneNames.includes(placeOfTable)) {
        return placeOfTable;
      }

      if (currentZoneName && zoneNames.includes(currentZoneName)) {
        return currentZoneName;
      }

      return zoneNames[0];
    });
  }, [zoneNamesKey, placeOfTable]);

  useEffect(() => {
    const loadReservationLayoutOverrides = async () => {
      const { restaurantId, reservationId } = getReservationLayoutContext();

      if (!show || !restaurantId || !reservationId) {
        setReservationLayoutOverrides([]);
        setReservationLayoutDraftItems([]);
        setFloorToolMode("select");
        return;
      }

      try {
        setIsLoadingReservationLayout(true);

        const response = await api.get(
          `restaurants/${restaurantId}/reservation-table-layout/${reservationId}/`,
        );

        setReservationLayoutOverrides(
          Array.isArray(response.data) ? response.data : [],
        );

        setReservationLayoutDraftItems([]);
        setFloorToolMode("select");
      } catch (error) {
        console.error("Failed to load reservation layout overrides:", error);
        setReservationLayoutOverrides([]);
        setReservationLayoutDraftItems([]);
        setFloorToolMode("select");
      } finally {
        setIsLoadingReservationLayout(false);
      }
    };

    loadReservationLayoutOverrides();
  }, [
    show,
    reservation?.id,
    reservation?.reservation_id,
    reservation?.pk,
    reservation?.restaurant,
    reservation?.restaurant_id,
    all_tables,
  ]);

  const configSumFor = (numOfTables) => {
    const cfg = tablesConfigList || [];
    const get = (i) => (Number.isFinite(Number(cfg?.[i])) ? Number(cfg[i]) : 0);

    let configSum = 0;

    if (numOfTables >= 2) configSum += get(0);
    if (numOfTables >= 3) configSum += get(1);
    if (numOfTables >= 4) configSum += get(2);

    for (let i = 5; i <= numOfTables; i++) {
      configSum += get(((i + 1) % 2) + 1);
    }

    return configSum;
  };

  const getSelectedTableNumbers = (tablesByZone = {}) => {
    const selectedTableNumbers = [];

    Object.keys(tablesByZone || {}).forEach((zoneKey) => {
      (tablesByZone[zoneKey] || []).forEach((table) => {
        if (table?.number !== undefined && table?.number !== null) {
          selectedTableNumbers.push(String(table.number));
        }
      });
    });

    return selectedTableNumbers;
  };

  const hasSelectedTableNumbers = (tablesByZone = {}) => {
    return getSelectedTableNumbers(tablesByZone).length > 0;
  };

  const setSelectedTablesFromSource = (nextSelectedTables, source) => {
    selectedTablesChangeSourceRef.current = source;
    setSelectedTables(nextSelectedTables || {});
  };

  const buildAvailabilityKey = () => {
    return (tables_list || [])
      .map((zoneRow) =>
        Array.isArray(zoneRow) ? zoneRow.join("|") : String(zoneRow),
      )
      .join("||");
  };

  const buildSuggestedTablesForZone = (zoneName) => {
    if (!zoneName) return null;
    if (!Array.isArray(tables_list) || tables_list.length === 0) return null;
    if (!Array.isArray(all_tables) || all_tables.length === 0) return null;

    const targetPeople = Number(numPeople);

    if (!Number.isFinite(targetPeople) || targetPeople <= 0) {
      return null;
    }

    const zoneRow = tables_list.find(
      (z) => String(z?.[0] ?? "").split(";")[0] === String(zoneName),
    );

    if (!zoneRow) return null;

    const seatMap = new Map(
      all_tables.map((t) => [
        String(t.id),
        parseInt(t.number_of_seats, 10) || 0,
      ]),
    );

    const tableDataById = new Map();

    const availableIds = zoneRow
      .slice(1)
      .map((s) => {
        const [idRaw, statusRaw, reservationIdsRaw] = String(s).split(";");
        const id = String(idRaw ?? "").trim();

        const reservationIds = reservationIdsRaw
          ? String(reservationIdsRaw)
              .split(",")
              .map((reservationId) => reservationId.trim())
              .filter(
                (reservationId) =>
                  reservationId &&
                  reservationId !== "null" &&
                  reservationId !== "undefined" &&
                  reservationId !== "None",
              )
          : [];

        const tableLike = {
          number: id,
          status: String(statusRaw ?? "0").trim(),
          reservationIds,
        };

        tableDataById.set(id, tableLike);

        return tableLike;
      })
      .filter((tableLike) => {
        return !isTableBlockedForCurrentReservation(tableLike, reservation);
      })
      .map((tableLike) => tableLike.number);

    const candidates = availableIds
      .map((id) => ({
        id,
        seats: seatMap.get(String(id)) || 0,
      }))
      .filter((candidate) => candidate.seats > 0);

    if (candidates.length === 0) return null;

    const N = candidates.length;
    const maxSum = candidates.reduce(
      (acc, candidate) => acc + candidate.seats,
      0,
    );

    const dp = Array.from({ length: N + 1 }, () => new Uint8Array(maxSum + 1));

    const parentIdx = Array.from({ length: N + 1 }, () =>
      new Int16Array(maxSum + 1).fill(-1),
    );

    const parentSum = Array.from({ length: N + 1 }, () =>
      new Int32Array(maxSum + 1).fill(-1),
    );

    dp[0][0] = 1;

    for (let i = 0; i < N; i++) {
      const seats = candidates[i].seats;

      for (let count = Math.min(i + 1, N); count >= 1; count--) {
        for (let sum = maxSum; sum >= seats; sum--) {
          if (dp[count][sum]) continue;
          if (!dp[count - 1][sum - seats]) continue;

          dp[count][sum] = 1;
          parentIdx[count][sum] = i;
          parentSum[count][sum] = sum - seats;
        }
      }
    }

    let best = null;

    for (let count = 1; count <= N; count++) {
      const requiredSeats = Math.max(0, targetPeople - configSumFor(count));

      for (let sum = requiredSeats; sum <= maxSum; sum++) {
        if (!dp[count][sum]) continue;

        const capacity = sum + configSumFor(count);

        best = {
          count,
          sumSeats: sum,
          capacity,
        };

        break;
      }

      if (best) break;
    }

    if (!best) {
      let bestUnder = null;

      for (let count = 1; count <= N; count++) {
        for (let sum = maxSum; sum >= 0; sum--) {
          if (!dp[count][sum]) continue;

          const capacity = sum + configSumFor(count);

          if (
            !bestUnder ||
            capacity > bestUnder.capacity ||
            (capacity === bestUnder.capacity && count < bestUnder.count)
          ) {
            bestUnder = {
              count,
              sumSeats: sum,
              capacity,
            };
          }

          break;
        }
      }

      best = bestUnder;
    }

    if (!best) return null;

    let chosenIds = [];
    let count = best.count;
    let sum = best.sumSeats;

    while (count > 0) {
      const index = parentIdx[count][sum];

      if (index < 0) break;

      chosenIds.push(candidates[index].id);
      sum = parentSum[count][sum];
      count--;
    }

    chosenIds = [...new Set(chosenIds.map(String))];

    if (chosenIds.length === 0) return null;

    return {
      [zoneName]: chosenIds.map((id) => {
        const tableData = tableDataById.get(String(id));

        return {
          number: String(id),
          status: tableData?.status || "0",
          reservationIds: tableData?.reservationIds || [],
        };
      }),
    };
  };

  const initialSelectedTablesKey = JSON.stringify(initialSelectedTables || {});

  useEffect(() => {
    const reservationKey = [
      getCurrentReservationId(reservation),
      reservation?.token || "",
      date || "",
      time || "",
    ].join("|");

    const hydrationKey = `${reservationKey}|${initialSelectedTablesKey}`;

    if (lastHydratedInitialTablesKeyRef.current === hydrationKey) {
      return;
    }

    const isDifferentReservation =
      lastHydratedReservationKeyRef.current !== reservationKey;

    lastHydratedReservationKeyRef.current = reservationKey;
    lastHydratedInitialTablesKeyRef.current = hydrationKey;

    const nextInitialSelectedTables = initialSelectedTables || {};

    setSelectedTablesFromSource(nextInitialSelectedTables, "hydrate");

    if (isDifferentReservation) {
      lastSeenPlaceOfTableRef.current = String(placeOfTable || "").trim();

      if (placeOfTable) {
        setActiveZoneName(placeOfTable);
      }

      pendingPlaceSuggestionRef.current = false;
      lastAppliedPlaceSuggestionKeyRef.current = "";
      skipNextPlaceSuggestionRef.current = false;
    }
  }, [
    initialSelectedTablesKey,
    reservation?.id,
    reservation?.reservation_id,
    reservation?.pk,
    reservation?.token,
    date,
    time,
  ]);

  useEffect(() => {
    const currentPlace = String(placeOfTable || "").trim();

    if (!currentPlace) return;

    const previousPlace = String(lastSeenPlaceOfTableRef.current || "").trim();
    const placeActuallyChanged = previousPlace !== currentPlace;

    if (placeActuallyChanged) {
      placeChangeVersionRef.current += 1;
      lastSeenPlaceOfTableRef.current = currentPlace;
      pendingPlaceSuggestionRef.current = true;
      lastAppliedPlaceSuggestionKeyRef.current = "";
      setActiveZoneName(currentPlace);
    }

    if (!pendingPlaceSuggestionRef.current) return;

    const reservationId = getCurrentReservationId(reservation);

    const suggestionKey = [
      placeChangeVersionRef.current,
      currentPlace,
      reservationId,
      Number(numPeople) || 0,
      buildAvailabilityKey(),
      JSON.stringify(tablesConfigList || []),
    ].join("|");

    if (lastAppliedPlaceSuggestionKeyRef.current === suggestionKey) return;

    if (skipNextPlaceSuggestionRef.current) {
      skipNextPlaceSuggestionRef.current = false;
      pendingPlaceSuggestionRef.current = false;
      lastAppliedPlaceSuggestionKeyRef.current = suggestionKey;
      return;
    }

    const suggestedTables = buildSuggestedTablesForZone(currentPlace);

    if (!suggestedTables) {
      return;
    }

    pendingPlaceSuggestionRef.current = false;
    lastAppliedPlaceSuggestionKeyRef.current = suggestionKey;

    if (!isEqual(suggestedTables, selectedTablesRef.current || {})) {
      setSelectedTablesFromSource(suggestedTables, "auto");
    }
  }, [
    placeOfTable,
    tables_list,
    all_tables,
    numPeople,
    reservation,
    tablesConfigList,
  ]);

  useEffect(() => {
    const reservationId = getCurrentReservationId(reservation);
    const currentPeople = Number(numPeople);

    if (
      !reservationId ||
      !Number.isFinite(currentPeople) ||
      currentPeople <= 0
    ) {
      return;
    }

    const lastSnapshot = lastPeopleSnapshotRef.current;

    if (lastSnapshot.reservationId !== reservationId) {
      lastPeopleSnapshotRef.current = {
        reservationId,
        numPeople: currentPeople,
      };
      lastScenarioRef.current = "";
      return;
    }

    if (Number(lastSnapshot.numPeople) === currentPeople) {
      return;
    }

    if (!reservation || !date || !time) return;
    if (!Array.isArray(tables_list) || tables_list.length === 0) return;
    if (!Array.isArray(all_tables) || all_tables.length === 0) return;

    const markPeopleChangeHandled = () => {
      lastPeopleSnapshotRef.current = {
        reservationId,
        numPeople: currentPeople,
      };
    };

    const targetPeople = currentPeople;

    const availabilityKey = tables_list
      .map((z) => (Array.isArray(z) ? z.join("|") : String(z)))
      .join("||");

    const scenarioKey = `${reservationId}|people:${lastSnapshot.numPeople}->${currentPeople}|${
      date ?? ""
    }|${time ?? ""}|${placeOfTable ?? ""}|${availabilityKey}`;

    if (lastScenarioRef.current === scenarioKey) {
      return;
    }

    const activeZone =
      placeOfTable ||
      activeZoneName ||
      Object.keys(selectedTablesRef.current || {})[0];

    if (!activeZone) {
      return;
    }

    const zoneRow = tables_list.find(
      (z) => String(z?.[0] ?? "").split(";")[0] === String(activeZone),
    );

    if (!zoneRow) {
      return;
    }

    const seatMap = new Map(
      all_tables.map((t) => [
        String(t.id),
        parseInt(t.number_of_seats, 10) || 0,
      ]),
    );

    const freeIds = zoneRow
      .slice(1)
      .map((s) => String(s).split(";"))
      .filter(([, status, reservationIdsRaw]) => {
        const reservationIds = reservationIdsRaw
          ? String(reservationIdsRaw)
              .split(",")
              .map((id) => id.trim())
              .filter(
                (id) =>
                  id && id !== "null" && id !== "undefined" && id !== "None",
              )
          : [];

        const tableLike = {
          status: String(status).trim(),
          reservationIds,
        };

        return !isTableBlockedForCurrentReservation(tableLike, reservation);
      })
      .map(([id]) => String(id).trim());

    const candidates = freeIds
      .map((id) => ({ id, seats: seatMap.get(id) || 0 }))
      .filter((c) => c.seats > 0);

    if (candidates.length === 0) {
      lastScenarioRef.current = scenarioKey;
      markPeopleChangeHandled();
      return;
    }

    const N = candidates.length;
    const maxSum = candidates.reduce((acc, c) => acc + c.seats, 0);

    const dp = Array.from({ length: N + 1 }, () => new Uint8Array(maxSum + 1));

    const parentIdx = Array.from({ length: N + 1 }, () =>
      new Int16Array(maxSum + 1).fill(-1),
    );

    const parentSum = Array.from({ length: N + 1 }, () =>
      new Int32Array(maxSum + 1).fill(-1),
    );

    dp[0][0] = 1;

    for (let i = 0; i < N; i++) {
      const w = candidates[i].seats;

      for (let c = Math.min(i + 1, N); c >= 1; c--) {
        for (let s = maxSum; s >= w; s--) {
          if (dp[c][s]) continue;
          if (!dp[c - 1][s - w]) continue;

          dp[c][s] = 1;
          parentIdx[c][s] = i;
          parentSum[c][s] = s - w;
        }
      }
    }

    let best = null;

    for (let c = 1; c <= N; c++) {
      const reqSeats = Math.max(0, targetPeople - configSumFor(c));

      for (let s = reqSeats; s <= maxSum; s++) {
        if (!dp[c][s]) continue;

        const cap = s + configSumFor(c);
        best = { count: c, sumSeats: s, cap, reach: true };
        break;
      }

      if (best) break;
    }

    if (!best) {
      let bestUnder = null;

      for (let c = 1; c <= N; c++) {
        for (let s = maxSum; s >= 0; s--) {
          if (!dp[c][s]) continue;

          const cap = s + configSumFor(c);
          if (cap >= targetPeople) continue;

          if (
            !bestUnder ||
            cap > bestUnder.cap ||
            (cap === bestUnder.cap && c < bestUnder.count)
          ) {
            bestUnder = { count: c, sumSeats: s, cap };
          }

          break;
        }
      }

      if (bestUnder) {
        best = { ...bestUnder, reach: false };
      }
    }

    let chosenIds = [];

    if (best) {
      let c = best.count;
      let s = best.sumSeats;

      while (c > 0) {
        const idx = parentIdx[c][s];

        if (idx < 0) break;

        chosenIds.push(candidates[idx].id);
        s = parentSum[c][s];
        c--;
      }
    }

    chosenIds = [...new Set(chosenIds.map(String))];

    const nextSelectedTables =
      chosenIds.length > 0
        ? {
            [activeZone]: chosenIds.map((id) => ({
              number: String(id),
              status: "0",
              reservationIds: [],
            })),
          }
        : {};

    lastScenarioRef.current = scenarioKey;
    markPeopleChangeHandled();

    if (
      hasSelectedTableNumbers(nextSelectedTables) &&
      !isEqual(nextSelectedTables, selectedTablesRef.current || {})
    ) {
      setSelectedTablesFromSource(nextSelectedTables, "auto");
    }
  }, [
    numPeople,
    date,
    time,
    placeOfTable,
    activeZoneName,
    tables_list,
    all_tables,
    tablesConfigList,
    reservation,
  ]);

  useEffect(() => {
    if (connectTablesConfig) {
      const table_config = Object.values(connectTablesConfig).map(
        (config) => config.number_of_chairs,
      );

      setTablesConfigList(table_config);
    }
  }, [connectTablesConfig]);

  useEffect(() => {
    if (!all_tables) return;

    const selectedTableNumbers = getSelectedTableNumbers(selectedTables);

    const filteredTables = all_tables.filter((table) =>
      selectedTableNumbers.includes(table.id.toString()),
    );

    const totalSeats = filteredTables.reduce(
      (acc, table) => acc + parseInt(table.number_of_seats, 10),
      0,
    );

    const numOfTables = filteredTables.length;
    const configSum = configSumFor(numOfTables);
    const nextCountPeople = totalSeats + configSum;
    const nextEnoughSeats = nextCountPeople >= Number(numPeople || 0);

    setEnoughSeats(nextEnoughSeats);
    setCountPeople(nextCountPeople);

    if (typeof setEnoughSeatsReservation === "function") {
      setEnoughSeatsReservation(nextEnoughSeats);
    }

    const source = selectedTablesChangeSourceRef.current;
    const hasTables = selectedTableNumbers.length > 0;

    const shouldWriteTablesToParent = hasTables || source === "user";

    const writeKey = [
      selectedTableNumbers.join(","),
      JSON.stringify(selectedTables || {}),
    ].join("|");

    if (
      shouldWriteTablesToParent &&
      lastSelectedTablesWriteKeyRef.current !== writeKey
    ) {
      lastSelectedTablesWriteKeyRef.current = writeKey;

      if (typeof handleTablesUsedChange === "function") {
        handleTablesUsedChange(selectedTableNumbers);
      }

      if (typeof setSelectedTablesSave === "function") {
        setSelectedTablesSave(selectedTables);
      }
    }

    selectedTablesChangeSourceRef.current = "none";
  }, [
    selectedTables,
    all_tables,
    numPeople,
    tablesConfigList,
    handleTablesUsedChange,
    setSelectedTablesSave,
    setEnoughSeatsReservation,
  ]);

  const handleTableSelect = (table, zone) => {
    const zoneChanged = String(placeOfTable) !== String(zone);

    if (zoneChanged) {
      skipNextPlaceSuggestionRef.current = true;
      setPlaceOfTable(zone);
      setActiveZoneName(zone);
    }

    selectedTablesChangeSourceRef.current = "user";

    setSelectedTables((prevSelectedTables) => {
      const updatedTables = zoneChanged ? {} : { ...prevSelectedTables };

      if (updatedTables[zone]?.some((t) => t.number === table.number)) {
        updatedTables[zone] = updatedTables[zone].filter(
          (t) => t.number !== table.number,
        );

        if (updatedTables[zone].length === 0) {
          delete updatedTables[zone];
        }
      } else {
        updatedTables[zone] = [
          ...(updatedTables[zone] || []),
          {
            number: table.number,
            status: table.status,
            reservationIds: table.reservationIds || [],
          },
        ];
      }

      return updatedTables;
    });
  };

  const handleZoneChange = (zoneName) => {
    setActiveZoneName(zoneName);
    setPlaceOfTable(zoneName);

    const suggestedTables = buildSuggestedTablesForZone(zoneName);

    if (
      suggestedTables &&
      !isEqual(suggestedTables, selectedTablesRef.current || {})
    ) {
      pendingPlaceSuggestionRef.current = false;
      setSelectedTablesFromSource(suggestedTables, "auto");
    }
  };

  const handleReservationLayoutItemChange = (layoutItem) => {
    setReservationLayoutDraftItems((prevItems) => {
      const nextItems = [...prevItems];

      const existingIndex = nextItems.findIndex(
        (item) => String(item.table_id) === String(layoutItem.table_id),
      );

      if (existingIndex >= 0) {
        nextItems[existingIndex] = {
          ...nextItems[existingIndex],
          ...layoutItem,
        };
      } else {
        nextItems.push(layoutItem);
      }

      return nextItems;
    });
  };

  const discardReservationLayoutDraft = () => {
    setReservationLayoutDraftItems([]);
    setFloorToolMode("select");
    setShowUnsavedReservationLayoutModal(false);
  };

  const saveReservationLayoutDraft = async () => {
    if (!reservationLayoutDraftItems.length) return true;

    const { restaurantId, reservationId } = getReservationLayoutContext();

    if (!restaurantId || !reservationId) {
      console.error("Missing restaurant/reservation for saving layout.");
      return false;
    }

    const cleanLayoutItems = reservationLayoutDraftItems
      .filter((item) => item && item.table_id)
      .map((item) => ({
        table_id: Number(item.table_id),
        left: Number(item.left || 0),
        top: Number(item.top || 0),
        width: Number(item.width || 50),
        height: Number(item.height || 50),
        isPlaced: item.isPlaced !== false,
        rotation: Number(item.rotation || 0),
      }));

    if (!cleanLayoutItems.length) {
      setReservationLayoutDraftItems([]);
      return true;
    }

    try {
      setIsSavingReservationLayout(true);

      console.log("Saving reservation layout payload:", {
        restaurantId,
        reservationId,
        items: cleanLayoutItems,
      });

      const response = await api.patch(
        `restaurants/${restaurantId}/reservation-table-layout/${reservationId}/`,
        {
          items: cleanLayoutItems,
        },
      );

      const savedItems = Array.isArray(response.data?.layout_items)
        ? response.data.layout_items
        : Array.isArray(response.data?.items)
          ? response.data.items
          : Array.isArray(response.data)
            ? response.data
            : cleanLayoutItems;

      setReservationLayoutOverrides((prevItems) =>
        mergeLayoutItems(prevItems, savedItems),
      );

      setReservationLayoutDraftItems([]);
      setFloorToolMode("select");

      window.dispatchEvent(
        new CustomEvent("soup:reservation-layout-updated", {
          detail: {
            restaurantId: Number(restaurantId),
            reservationId: Number(reservationId),
            layoutItems: savedItems,
          },
        }),
      );

      return true;
    } catch (error) {
      console.error("Backend response:", error.response?.data);
      console.error("Status:", error.response?.status);
      console.error("Full Axios error:", error);
      return false;
    } finally {
      setIsSavingReservationLayout(false);
    }
  };

  const hasUnsavedReservationLayoutChanges =
    reservationLayoutDraftItems.length > 0;

  const requestCloseAssignmentModal = () => {
    if (hasUnsavedReservationLayoutChanges) {
      setShowUnsavedReservationLayoutModal(true);
      return;
    }

    onClose();
  };

  const handleConfirmAssignment = async () => {
    const layoutSaved = await saveReservationLayoutDraft();

    if (!layoutSaved) return;

    onClose();
  };

  const forceCloseAssignmentModal = () => {
    discardReservationLayoutDraft();
    onClose();
  };

  const isMoveMode = tableViewMode === "floor" && floorToolMode === "move";

  const activeZoneTables =
    tables.find(
      (zoneTables) =>
        String(zoneTables?.[0]?.number || "") === String(activeZoneName),
    ) || tables[0];

  const activeRenderedZoneName = activeZoneTables?.[0]?.number || "defaultKey";

  const activeAvailabilityTables = activeZoneTables
    ? activeZoneTables.slice(1)
    : [];

  return (
    <>
      <ModalStackingFix />

      <CustomModal
        show={show}
        onHide={requestCloseAssignmentModal}
        title="Atribuir mesas"
        centered={false}
        footer={
          <>
            <LegendContainer>
              <LegendItem>
                <LegendColor color="#FFFFFF" /> Livre
              </LegendItem>

              <LegendItem>
                <LegendColor color="#99B0AD" /> Ocupada
              </LegendItem>

              <LegendItem>
                <LegendColor color="#f79574" /> Selecionada
              </LegendItem>

              <LegendItem>
                <LegendColor color="#EE561B" /> Selecionada mas ocupada
              </LegendItem>
            </LegendContainer>

            <ModernButton
              color="#99B0AD"
              onClick={requestCloseAssignmentModal}
              disabled={isSavingReservationLayout}
            >
              Cancelar
            </ModernButton>

            <ModernButton
              color="#de8c3a"
              onClick={handleConfirmAssignment}
              style={{ marginLeft: "8px" }}
              disabled={isSavingReservationLayout}
            >
              {isSavingReservationLayout
                ? "A guardar…"
                : "Atribuir mesas selecionadas"}
            </ModernButton>
          </>
        }
      >
        <Row style={{ width: "100%" }}>
          <Col
            className="d-flex justify-content-center"
            xs={12}
            style={{ marginTop: "5px", marginBottom: "10px" }}
          >
            <StyledH6 enoughSeats={enoughSeats}>
              <FontAwesomeIcon
                icon={faUserGroup}
                style={{
                  color: enoughSeats ? "green" : "red",
                  marginRight: "8px",
                }}
              />
              <strong>{countPeople} lugares</strong> atribuídos para{" "}
              <strong>{numPeople} pessoas</strong>
            </StyledH6>
          </Col>
        </Row>

        {is_production && (
          <ViewModeBox>
            <div>
              <div style={{ fontWeight: 700 }}>
                {tableViewMode === "floor"
                  ? isMoveMode
                    ? "Modo visual"
                    : "Modo visual"
                  : "Modo simples"}
              </div>

              <ViewModeText>
                {tableViewMode === "floor"
                  ? isMoveMode
                    ? "Modo mover ativo: arrasta mesas para ajustar a planta desta reserva. As posições serão guardadas quando clicares em Atribuir mesas selecionadas."
                    : "Modo selecionar ativo: clica nas mesas para atribuir ou remover da reserva."
                  : ""}

                {reservationLayoutDraftItems.length > 0 && (
                  <>
                    {" "}
                    <strong>
                      ({reservationLayoutDraftItems.length} alteração/alterações
                      de posição por guardar)
                    </strong>
                  </>
                )}
              </ViewModeText>
            </div>

            <ViewModeButtons>
              <Button
                size="sm"
                variant={
                  tableViewMode === "simple" ? "secondary" : "outline-secondary"
                }
                onClick={() => setTableViewMode("simple")}
                disabled={isSavingReservationLayout}
              >
                Vista simples
              </Button>

              <Button
                size="sm"
                variant={
                  tableViewMode === "floor" ? "secondary" : "outline-secondary"
                }
                onClick={() => setTableViewMode("floor")}
                disabled={isSavingReservationLayout}
              >
                Planta visual
              </Button>

              {tableViewMode === "floor" && (
                <>
                  <ToolDivider />

                  <ToolIconButton
                    type="button"
                    active={floorToolMode === "select"}
                    onClick={() => setFloorToolMode("select")}
                    title="Selecionar mesas — clica nas mesas para atribuir ou remover da reserva."
                    disabled={isSavingReservationLayout}
                    aria-label="Selecionar mesas"
                  >
                    ↖
                  </ToolIconButton>

                  <ToolIconButton
                    type="button"
                    active={floorToolMode === "move"}
                    onClick={() => setFloorToolMode("move")}
                    title="Mover mesas desta reserva — arrasta mesas para ajustar a planta desta reserva."
                    disabled={isSavingReservationLayout}
                    aria-label="Mover mesas desta reserva"
                  >
                    ✥
                  </ToolIconButton>
                </>
              )}
            </ViewModeButtons>
          </ViewModeBox>
        )}

        {zoneNames.length > 1 && (
          <ZonePickerBox>
            <ZonePickerLabel>Sala:</ZonePickerLabel>

            {zoneNames.length <= 4 ? (
              zoneNames.map((zoneName) => (
                <ZoneBubble
                  key={zoneName}
                  type="button"
                  active={String(activeZoneName) === String(zoneName)}
                  onClick={() => handleZoneChange(zoneName)}
                >
                  {zoneName}
                </ZoneBubble>
              ))
            ) : (
              <ZoneSelect
                value={activeZoneName}
                onChange={(event) => handleZoneChange(event.target.value)}
              >
                {zoneNames.map((zoneName) => (
                  <option key={zoneName} value={zoneName}>
                    {zoneName}
                  </option>
                ))}
              </ZoneSelect>
            )}
          </ZonePickerBox>
        )}

        {tables && all_tables ? (
          <>
            {activeZoneTables ? (
              <div key={activeRenderedZoneName} style={{ marginBottom: 18 }}>
                <h5 style={{ marginBottom: 10 }}>{activeRenderedZoneName}</h5>

                {tableViewMode === "floor" ? (
                  <>
                    {isLoadingReservationLayout && (
                      <div
                        style={{
                          fontSize: "0.85rem",
                          color: "#777",
                          marginBottom: 8,
                        }}
                      >
                        A carregar posições específicas desta reserva…
                      </div>
                    )}

                    <ReservationFloorPlan
                      allTables={all_tables}
                      zoneName={activeRenderedZoneName}
                      availabilityTables={activeAvailabilityTables}
                      selectedTables={selectedTables}
                      onSelectTable={handleTableSelect}
                      layoutOverrides={reservationLayoutOverrides}
                      layoutDraftItems={reservationLayoutDraftItems}
                      enableMove={isMoveMode}
                      onLayoutItemChange={handleReservationLayoutItemChange}
                    />
                  </>
                ) : (
                  <ZoneContainer>
                    {activeAvailabilityTables.map((table) => {
                      const isSelected = selectedTables?.[
                        activeRenderedZoneName
                      ]?.some((t) => t.number === table.number);

                      const the_table = all_tables.find(
                        (t) => t.id.toString() === table.number,
                      );

                      const isBlocked = isTableBlockedForCurrentReservation(
                        table,
                        reservation,
                      );

                      return (
                        <StyledTableDiv
                          key={table.number}
                          isSelected={isSelected}
                          tableStatus={table.status}
                          onClick={() =>
                            handleTableSelect(table, activeRenderedZoneName)
                          }
                          title={[
                            `Mesa ${
                              the_table?.table_name ||
                              the_table?.table_number ||
                              table.number
                            }`,
                            getReservationInfoText(table),
                            isBlocked
                              ? "Esta mesa já está ocupada por outra reserva neste horário. Ao guardar, será feita a troca."
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" — ")}
                        >
                          {the_table?.table_name ||
                            the_table?.table_number ||
                            table.number}
                        </StyledTableDiv>
                      );
                    })}
                  </ZoneContainer>
                )}
              </div>
            ) : null}
          </>
        ) : (
          <Loader />
        )}
      </CustomModal>

      <Modal
        show={showUnsavedReservationLayoutModal}
        onHide={() => setShowUnsavedReservationLayoutModal(false)}
        centered
        className="unsaved-reservation-layout-modal"
        backdropClassName="unsaved-reservation-layout-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Sair sem guardar posições?</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Alteraste a posição de mesas desta reserva, mas ainda não guardaste.
          Se saíres agora, essas alterações de posição serão descartadas.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowUnsavedReservationLayoutModal(false)}
            disabled={isSavingReservationLayout}
          >
            Continuar edição
          </Button>

          <Button
            variant="outline-danger"
            onClick={forceCloseAssignmentModal}
            disabled={isSavingReservationLayout}
          >
            Sair sem guardar
          </Button>

          <Button
            variant="success"
            onClick={handleConfirmAssignment}
            disabled={isSavingReservationLayout}
          >
            {isSavingReservationLayout ? "A guardar…" : "Guardar e sair"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TablesModal;
